import { Alert } from 'react-bootstrap';
import type { InjectedRouter } from 'react-router';

import { LoaderButton } from '../../generic/Button';
import { FormGroup, LabeledTextInput } from '../../generic/Forms';
import PasswordMeter from '../../generic/PasswordMeter';
import SignInSignUpToggle from '../common/SignInSignUpToggle';
import usePasswordReset from '../context/usePasswordReset';
import AuthForm from '../form';
import CenteredLayout from '../layouts/Centered';
import type { PasswordStrength, PasswordStrengthLabel } from '../../../helpers/passwordStrength';
import type { RouterProps } from '../../../types';

function getButtonText(hasSentResetEmail: boolean, showResendText: boolean): string {
  if (showResendText) {
    return 'Resend';
  }
  return hasSentResetEmail
    ? 'Change Password'
    : 'Send Password Reset Email';
}

type PasswordResetProps = {
  location: RouterProps['location'];
  router: InjectedRouter;
};

/**
 * Page for resetting password.
 *
 * Resetting the password is a two-step process. First the user enters their email and is
 * sent a password reset token. Clicking the link will send them back to this page
 * with both a token + email as query params, which are then used for the next step.
 */
const PasswordReset = ({ location, router }: PasswordResetProps) => {
  const {
    email: {
      ref: emailInputRef,
      ...email
    },
    password: {
      ref: passwordInputRef,
      ...password
    },
    hasSentResetEmail,
    isRequestPending,
    isSubmitDisabled,
    passwordStrength,
    onSubmit,
    alert,
    showResendText,
  } = usePasswordReset({ location, router });
  return (
    <CenteredLayout>
      <AuthForm.Container onSubmit={onSubmit}>
        <AuthForm.Heading heading="Reset Password" />
        {alert && (
          <Alert bsStyle={alert.type}>
            {alert.message}
          </Alert>
        )}
        <FormGroup>
          <LabeledTextInput
            ref={emailInputRef}
            label="Email"
            placeholder="Enter your email"
            value={email.value}
            isDisabled={hasSentResetEmail}
            onChange={email.onChange}
          />
          {hasSentResetEmail && (
            <AuthForm.PasswordInput
              ref={passwordInputRef}
              password={password}
              hidePasswordInput={!hasSentResetEmail}
              step="authenticate"
              passwordStrength={passwordStrength as PasswordStrength}
              onSubmit={onSubmit}
            />
          )}
        </FormGroup>
        {hasSentResetEmail && (
          <div className="margin-top-xs margin-bottom-lg">
            <PasswordMeter strength={passwordStrength?.label as PasswordStrengthLabel} />
          </div>
        )}
        <LoaderButton
          type="primary"
          size="large"
          isLoading={isRequestPending}
          isDisabled={isSubmitDisabled || isRequestPending}
          onClick={onSubmit}
          isFullWidth
        >
          {getButtonText(hasSentResetEmail, showResendText)}
        </LoaderButton>
      </AuthForm.Container>
      <SignInSignUpToggle
        page="register"
        email={email.value}
      />
    </CenteredLayout>
  );
};

export default PasswordReset;
